/**
 * @file VideoUploader.tsx
 *
 * @description A component that allows police officers to upload video evidence files to the system.
 * The component handles multiple file uploads, progress tracking, and server communication.
 *
 * @module Components
 */
import { CloudUpload as CloudUploadIcon } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import React, { useRef } from "react";
import { useToast } from "../../shared/toast";
import { useVideoUpload } from "./hooks/useVideoUpload";
import { UploadProgressDisplay } from "./components/UploadProgressDisplay";

interface VideoUploaderProps {
  fetchVideos: () => void;
  allowUpload: boolean;
}

const ALLOWED_UPLOAD_EXTENSIONS = [
  ".mp4",
  ".mpeg",
  ".mp3",
  ".wav",
  ".mov",
  ".zip",
];

const VideoUploader: React.FC<VideoUploaderProps> = ({
  fetchVideos,
  allowUpload,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toast = useToast();

  const { uploading, uploadJobs, startUpload } = useVideoUpload({
    fetchVideos,
    showError: toast.error,
    showSuccess: toast.success,
  });

  if (!allowUpload) return null;

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Validate file types
    const invalidFiles = Array.from(files).filter((file) => {
      const name = file.name.toLowerCase();
      return !ALLOWED_UPLOAD_EXTENSIONS.some((extension) =>
        name.endsWith(extension)
      );
    });

    if (invalidFiles.length > 0) {
      toast.error(
        "Only .mp4, .mpeg, .mp3, .wav, .mov, and .zip files are allowed."
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    await startUpload(files);
    
    // If started successfully, we keep the input value? 
    // Usually we clear it so change event fires again if user selects same file.
    // But if uploading, we disable the button.
    // The hook clears state on completion.
    
    if (fileInputRef.current) {
        // Always reset input to allow re-selection
        fileInputRef.current.value = "";
    }
  };

  return (
    <Box>
      {/* Upload progress indicators */}
      <UploadProgressDisplay uploadJobs={uploadJobs} />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_UPLOAD_EXTENSIONS.join(",")}
        multiple
        style={{ display: "none" }}
        onChange={handleFileSelect}
        id="video-upload-input"
      />

      {/* Upload button */}
      <label htmlFor="video-upload-input">
        <Button
          variant="contained"
          component="span"
          startIcon={<CloudUploadIcon />}
          disabled={uploading}
          sx={{
            textTransform: "none",
            bgcolor: "#2e7d32",
            "&:hover": {
              bgcolor: "#1b5e20",
            },
          }}
        >
          {uploading ? "Uploading..." : "Upload Video"}
        </Button>
      </label>
    </Box>
  );
};

export default VideoUploader;
